import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SectionHeading from '../components/SectionHeading';
import { storyTimeline, values, mediaAssets, seoByRoute, teamMembers, certificates, testimonials } from '../data/siteContent';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.08 * i, ease: 'easeOut' },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: (i = 1) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, delay: 0.06 * i, ease: 'easeOut' },
  }),
};

export default function About() {
  const seo = seoByRoute['/about'];

  return (
    <>
      <SEO {...seo} />

      {/* Hero section with prominent video */}
      <section className="mx-auto max-w-7xl px-6 pt-32 pb-16">
        <SectionHeading
          as="h1"
          className="mb-12"
          eyebrow="About Duoley"
          title="Our Story"
          description="Founded with a vision to bring the highest quality dairy and juice products to Ethiopia and beyond, Duoley Food Processing PLC has grown from a small local producer to a recognized brand in the industry."
        />

        {/* Prominent YouTube video section */}
        <motion.div
          className="mb-16 overflow-hidden rounded-3xl group shadow-2xl shadow-primary/20"
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="relative aspect-video bg-primary rounded-3xl overflow-hidden">
            <iframe
              src={mediaAssets.processingLineYoutube}
              title="Duoley Processing Line"
              className="h-full w-full border-0 group-hover:scale-105 transition-transform duration-500"
              allow="encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
        </motion.div>

        {/* Two-column: narrative + values */}
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Left: narrative + values */}
          <motion.div
            className="space-y-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <p className="text-lg text-slate-700 leading-relaxed">
              Duoley Food Processing PLC was established with a vision to bring premium dairy and juice products to the Ethiopian market. We started production of milk and juice products, focusing on quality and natural ingredients. Today, we are expanding our product range and distribution network across Ethiopia and beyond.
            </p>

            {/* Values card */}
            <motion.div
              className="rounded-3xl bg-white p-8 border border-slate-200 shadow-lg shadow-slate-200/40"
              variants={scaleIn}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="h-1.5 w-14 bg-primary rounded-full" />
                <p className="text-xs sm:text-sm uppercase tracking-[0.35em] text-tertiary font-semibold">
                  Our guiding values
                </p>
              </div>

              <ul className="mt-4 space-y-6">
                {values.map((value, idx) => (
                  <motion.li
                    key={value.title}
                    className="group relative pl-5"
                    variants={fadeUp}
                    custom={idx}
                  >
                    <p className="font-display text-lg text-text leading-6">
                      {value.title}
                    </p>
                    <p className="mt-2 text-sm text-slate-600 leading-6">
                      {value.description}
                    </p>

                    <span className="absolute -left-1 top-2 h-2 w-2 rounded-full bg-primary group-hover:scale-110 transition-transform" />
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </motion.div>

          {/* Right: Key stats and highlights */}
          <motion.div
            className="space-y-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <div className="rounded-3xl bg-gradient-to-br from-primary/10 to-secondary/10 p-8 border border-primary/20">
              <h3 className="font-display text-2xl text-text mb-6">Why Choose Duoley?</h3>
              <ul className="space-y-4">
                <li className="flex gap-3">
                  <span className="text-2xl">🌾</span>
                  <div>
                    <p className="font-semibold text-text">Farm-to-Table Excellence</p>
                    <p className="text-sm text-slate-600">Direct partnerships with 18+ community farmers</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-2xl">❄️</span>
                  <div>
                    <p className="font-semibold text-text">Cold-Chain Mastery</p>
                    <p className="text-sm text-slate-600">2-4°C maintained from farm to shelf</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="text-2xl">♻️</span>
                  <div>
                    <p className="font-semibold text-text">Circular Packaging</p>
                    <p className="text-sm text-slate-600">Eco-friendly, reusable, and sustainable</p>
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline section */}
      <section className="mx-auto mt-28 max-w-6xl px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <SectionHeading className="mb-10" eyebrow="Timeline" title="A timeline of impact" />

          <div className="rounded-3xl bg-gradient-to-br from-primary to-primary/90 px-6 sm:px-8 py-10 sm:py-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl -mr-40 -mt-40" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/10 rounded-full blur-3xl -ml-40 -mb-40" />

            <p className="max-w-3xl text-sm text-white leading-relaxed relative z-10">
              We honour every contributor, farmers, transporters, nutritionists, and educators. Whose collaboration fuels
              Duoley&apos;s growth. Explore key moments charting our journey.
            </p>

            <div className="mt-8 grid gap-5 md:grid-cols-2 relative z-10">
              {storyTimeline.map(({ year, event }, index) => (
                <motion.div
                  key={year}
                  className="flex gap-4 rounded-2xl bg-white/10 backdrop-blur-sm p-5 border border-white/15 hover:bg-white/15 transition"
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  custom={index}
                >
                  <div className="flex h-12 w-12 sm:h-14 sm:w-14 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-secondary/80 text-base sm:text-lg font-semibold text-white shadow-md">
                    {year}
                  </div>
                  <p className="text-sm text-white leading-relaxed">{event}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Meet Our Team Section */}
      <section className="mx-auto mt-32 max-w-6xl px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <SectionHeading
            className="mb-14"
            eyebrow="Our Team"
            title="Meet Our Team"
            description="Dedicated professionals committed to excellence, sustainability, and community empowerment."
          />

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                className="group rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-lg hover:shadow-xl hover:border-primary/30 transition-all duration-300"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                custom={index}
                whileHover={{ y: -4 }}
              >
                {/* Team Member Image */}
                <div className="relative overflow-hidden h-48 bg-gradient-to-br from-primary/20 to-secondary/20">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-6xl font-bold text-primary/30">
                      {member.name.charAt(0)}
                    </div>
                  )}
                </div>

                {/* Team Member Info */}
                <div className="p-6">
                  <h3 className="font-display text-lg text-text font-semibold">{member.name}</h3>
                  <p className="text-sm text-tertiary font-semibold mt-1">{member.role}</p>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Certificates Section */}
      <section className="mx-auto mt-32 max-w-6xl px-6 pb-16">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <SectionHeading
            className="mb-14"
            eyebrow="Recognition"
            title="Certificates & Appreciations"
            description="Recognitions from partners and organizations for our contributions and commitment to excellence."
          />

          <div className="grid gap-8 md:grid-cols-2">
            {certificates.map((cert, index) => (
              <motion.article
                key={cert.title}
                className="group rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-lg hover:shadow-2xl hover:border-primary/30 transition-all duration-300"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                custom={index}
                whileHover={{ y: -6 }}
              >
                {/* Certificate Image */}
                <div className="relative overflow-hidden h-48 bg-slate-100">
                  <img
                    src={cert.image}
                    alt={cert.title}
                    className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                {/* Certificate Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-display text-lg text-text font-semibold leading-tight">
                        {cert.title}
                      </h3>
                      <p className="text-sm text-secondary font-semibold mt-1">{cert.organization}</p>
                    </div>
                    <span className="text-2xl">🏆</span>
                  </div>

                  <p className="text-sm text-slate-600 leading-relaxed mb-4">
                    {cert.description}
                  </p>

                  <div className="space-y-2 pt-4 border-t border-slate-100">
                    <div className="text-xs text-slate-500">
                      <span className="font-semibold">Date:</span> {cert.date}
                    </div>
                    <div className="text-xs text-slate-500">
                      <span className="font-semibold">Signatory:</span> {cert.signatory}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Testimonials Section */}
      <section className="mx-auto max-w-6xl px-6 pb-32">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <SectionHeading
            className="mb-14"
            eyebrow="Testimonials"
            title="Recognized at the highest level"
            description="Leaders and partners who have visited and championed Duoley's mission."
          />

          <div className="grid gap-8 sm:grid-cols-2">
            {testimonials.map((testimonial, index) => (
              <motion.figure
                key={testimonial.name}
                className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/70 hover:shadow-2xl transition-all duration-300"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                custom={index}
                whileHover={{ y: -6 }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <figcaption className="px-6 py-5 text-center">
                  <p className="text-xs uppercase tracking-[0.3em] text-primary">{testimonial.title}</p>
                  <p className="mt-2 font-display text-lg text-text">{testimonial.name}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </motion.div>
      </section>
    </>
  );
}
